import { Link } from "react-router-dom";
import { PageHeader } from "@/shared/components/PageHeader";
import { Button } from "@/shared/components/ui/button";
import { KpiStrip, KpiTile } from "@/shared/components/ui/kpi";
import { StatusText } from "@/shared/components/ui/status-chip";
import { TableCard, TableHeadRow, TableRow, Th, Empty } from "@/shared/components/ui/table";
import { EmptyState } from "@/shared/components/ui/empty-state";
import { useCampaignsList } from "@/features/campaigns/use-campaigns";

const GRID = "grid-cols-[2fr_1fr_1fr_1fr_1fr]";

/**
 * Portfolio analytics (global nav) — performance rolled up across every
 * campaign, from the campaigns summary. Per-campaign engagement lives in each
 * campaign's Analytics tab.
 */
export function AnalyticsPage() {
  const { data, isLoading, isError, refetch } = useCampaignsList();
  const campaigns = data ?? [];

  const sum = (f: (c: (typeof campaigns)[number]) => number) => campaigns.reduce((a, c) => a + f(c), 0);
  const sent = sum((c) => c.sent);
  const opened = sum((c) => c.opened);
  const openRate = sent > 0 ? `${Math.round((opened / sent) * 100)}%` : "--";

  if (isError) {
    return (
      <div>
        <PageHeader title="Analytics" />
        <EmptyState
          message="Couldn't load portfolio analytics."
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  if (!isLoading && campaigns.length === 0) {
    return (
      <div>
        <PageHeader title="Analytics" />
        <EmptyState message="No campaigns yet — portfolio analytics appear once you run outreach." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Analytics"
        footnote="Portfolio performance across every campaign. Open the campaign to see its funnel and per-step engagement."
      />

      <KpiStrip>
        <KpiTile label="Campaigns" value={data ? String(campaigns.length) : "--"} />
        <KpiTile label="Contacts" value={data ? String(sum((c) => c.contacts)) : "--"} />
        <KpiTile label="Sent" value={data ? String(sent) : "--"} />
        <KpiTile label="Open rate" value={openRate} highlighted />
      </KpiStrip>

      <div className="mt-6">
        <TableCard>
          <TableHeadRow grid={GRID}>
            <Th>Campaign</Th>
            <Th>Status</Th>
            <Th>Contacts</Th>
            <Th>Sent</Th>
            <Th>Open rate</Th>
          </TableHeadRow>
          {campaigns.map((c) => {
            const rate = c.sent > 0 ? `${Math.round((c.opened / c.sent) * 100)}%` : null;
            return (
              <TableRow key={c.id} grid={GRID} data-testid={`portfolio-row-${c.id}`}>
                <Link to={`/campaigns/${c.id}`} className="font-sans text-[13.5px] font-semibold text-ge-primary hover:text-ge-accent">
                  {c.name}
                </Link>
                <div>
                  <StatusText tone={c.status === "LAUNCHED" ? "accent" : "faint"}>{c.status}</StatusText>
                </div>
                <div className="font-mono text-[12px] text-ge-value">{c.contacts || <Empty />}</div>
                <div className="font-mono text-[12px] text-ge-value">{c.sent || <Empty />}</div>
                <div className="font-mono text-[12px] text-ge-value">{rate ?? <Empty />}</div>
              </TableRow>
            );
          })}
        </TableCard>
      </div>
    </div>
  );
}
